import React, { useState } from 'react';
import { withStyles, Modal } from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import {
  deleteInfrastructureItem,
  setInfrastructureValues,
  restoreOriginalInfrastructure,
  createInfrastructureItem,
  updateInfrastructureList,
  searchInfrastructureList,
} from '~/store/dailyparts/actions';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import EditHistoryModal from '~/components/EditHistoryModal';
import EditHistoryButton from '~/components/EditHistoryButton';
import PF2MDataTable from '~/components/PF2MDataTable';
import {
  formatDate,
  formatTime,
  newMomentDate,
  parseDateTime,
  parseDateTimeToTimestamp,
} from '~/utils/moment';
import { usePagination } from '../hooks';

const INFRASTRUCTURE_EQUIP_TYPES = [21, 22, 23];
const MIXER = 21;
const PROJECTROBO = 22;
const PASTEFILL = 23;

const InfrastructureList = () => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const infrastructureList = useSelector(
    state => state.dailyParts.infrastructureList,
  );
  const filters = useSelector(state => state.dailyParts.filters);
  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const operationTypes = useSelector(state => state.manager.operationTypes);
  const infrastructureGroups = useSelector(
    state => state.manager.equipmentsGroups.filter(
      r => INFRASTRUCTURE_EQUIP_TYPES.includes(r.id_equipament),
    ),
    shallowEqual,
  );
  const infrastructureEquips = useSelector(
    state => state.manager.equipments.filter(r => INFRASTRUCTURE_EQUIP_TYPES.includes(r.id_equip)),
    shallowEqual,
  );
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const operatorsGroups = useSelector(
    state => state.manager.operatorsGroups.filter(o => o.name),
    shallowEqual,
  );
  const operators = useSelector(
    state => state.manager.operators.filter(o => o.name),
    shallowEqual,
  );
  const materials = useSelector(state => state.manager.materials);
  const exceptionTypes = useSelector(state => state.manager.exceptionTypes);
  const originalInfrastructureList = useSelector(
    state => state.dailyParts.originalInfrastructureList,
  );
  const infrastructurePagination = useSelector(
    state => state.dailyParts.pagination.infrastructurePagination,
  );

  const [openEditHistoryModal, setOpenEditHistoryModal] = useState(false);
  const [selectedEditHistoryData, setSelectedEditHistoryData] = useState(null);

  const calculateDuration = (rowData) => {
    const stDate = rowData.start_date;
    const stTime = rowData.start_time;
    const endDate = rowData.end_date;
    const endTime = rowData.end_time;

    const momentStart = parseDateTime(`${stDate} ${stTime}`, true);
    const momentEnd = parseDateTime(`${endDate} ${endTime}`, true);

    const diff = momentEnd.diff(momentStart, 'minutes', true);
    return diff.toFixed(2);
  };

  const validateLookupField = key => (rowData) => {
    if (['', '0', 0].includes(rowData[key])) {
      return { isValid: false, helperText: translate('validation:RequiredField') };
    }

    return { isValid: true };
  };

  const validateDateOrTimeField = key => (rowData) => {
    const currentDatetime = newMomentDate();
    const stDate = rowData.start_date || currentDatetime.format('DD/MM/YYYY');
    const stTime = rowData.start_time || currentDatetime.format('HH:mm:ss');
    const endDate = rowData.end_date || currentDatetime.format('DD/MM/YYYY');
    const endTime = rowData.end_time || currentDatetime.format('HH:mm:ss');

    const startDateTimestamp = parseDateTimeToTimestamp(stDate, stTime);
    const endDateTimestamp = parseDateTimeToTimestamp(endDate, endTime);

    if (key.startsWith('end_')) {
      if (endDateTimestamp < startDateTimestamp) {
        return {
          isValid: false,
          helperText: translate('validation:FinalDateSmallerThanInitialDate'),
        };
      }
      if (endDateTimestamp > currentDatetime.unix() * 1000) {
        return {
          isValid: false,
          helperText: translate('validation:FutureDateCantBeSelected'),
        };
      }
    }

    return { isValid: true };
  };

  const titles = [
    {
      title: 'ID',
      editable: 'never',
      field: 'id',
    },
    {
      editable: 'never',
      title: translate('common:EditHistory'),
      field: 'edit_history',
      render: row => (
        <EditHistoryButton
          exceptionType={row.exception_type}
          onClick={() => {
            setOpenEditHistoryModal(true);
            setSelectedEditHistoryData(row);
          }}
        />
      ),
    },
    {
      editable: 'always',
      title: translate('common:InitialDate'),
      field: 'start_date',
      customType: 'date',
      required: true,
      validate: validateDateOrTimeField('start_date'),
    },
    {
      editable: 'always',
      title: translate('common:FinalDate'),
      field: 'end_date',
      customType: 'date',
      required: true,
      validate: validateDateOrTimeField('end_date'),
    },
    {
      editable: 'always',
      title: translate('common:InitialHour'),
      field: 'start_time',
      customType: 'time',
      required: true,
      validate: validateDateOrTimeField('start_time'),
    },
    {
      editable: 'always',
      title: translate('common:FinalHour'),
      field: 'end_time',
      customType: 'time',
      required: true,
      validate: validateDateOrTimeField('end_time'),
    },
    {
      editable: 'never',
      title: translate('common:Duration'),
      field: 'duration',
      render: row => calculateDuration(row),
      validate: row => ({
        isValid: calculateDuration(row) >= 0,
      }),
    },
    {
      editable: 'always',
      title: translate('common:Shift'),
      field: 'turn_id',
      lookup: turns,
      required: true,
      customType: 'filterLookup',
      validate: validateLookupField('turn_id'),
    },
    {
      editable: 'always',
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
      required: true,
      customType: 'filterLookup',
      validate: validateLookupField('team_id'),
    },
    {
      editable: 'always',
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
      required: true,
      customType: 'filterLookup',
      tooltip: true,
      validate: validateLookupField('operator_group_id'),
    },
    {
      editable: 'always',
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
      required: true,
      customType: 'filterLookup',
      lookupFilter: (option, row) => option.id_group === row.operator_group_id
        || row.operator_group_id === 0,
      tooltip: true,
      validate: validateLookupField('operator_id'),
    },
    {
      editable: 'always',
      title: translate('common:EquipmentGroup'),
      field: 'equipament_groups_id',
      lookup: infrastructureGroups.filter(
        e => e.id_equipament === filters.equipmentType,
      ),
      required: true,
      customType: 'filterLookup',
      tooltip: true,
      validate: validateLookupField('equipament_groups_id'),
    },
    {
      editable: 'always',
      title: translate('common:Equipment'),
      field: 'group_equipament_links_id',
      lookup: infrastructureEquips.filter(
        e => e.id_equip === filters.equipmentType,
      ),
      required: true,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        option.id_group === row.equipament_groups_id
        || row.equipament_groups_id === 0
      ),
      validate: validateLookupField('group_equipament_links_id'),
    },
    {
      editable: 'always',
      title: translate('common:Material'),
      field: 'material_id',
      lookup: materials,
      required: true,
      customType: 'filterLookup',
      validate: validateLookupField('material_id'),
    },
    {
      editable: 'always',
      title: translate('common:LoadManager'),
      field: 'load_manager',
    },
    {
      editable: 'always',
      title: translate('common:LoadTime'),
      field: 'load_time',
    },
    {
      editable: 'always',
      title: translate('common:IdleTime'),
      field: 'idle_time',
    },
    {
      editable: 'always',
      title: translate('common:CodeTime'),
      field: 'code_time',
    },
    {
      editable: 'always',
      title: translate('common:Latitude'),
      field: 'load_lat',
    },
    {
      editable: 'always',
      title: translate('common:Longitude'),
      field: 'load_lon',
    },
    {
      editable: 'always',
      title: translate('common:Altitude'),
      field: 'load_alt',
    },
    {
      editable: 'never',
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
      customType: 'filterLookup',
      tooltip: true,
    },
  ];

  const mixerSpecificTitles = [
    {
      editable: 'always',
      title: translate('common:Origin'),
      field: 'id_mine',
      lookup: elements,
      required: true,
      customType: 'filterLookup',
      validate: validateLookupField('id_mine'),
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      field: 'id_mine_origin',
      lookup: subElements,
      required: true,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        option.id_element === row.id_mine || row.id_mine === 0
      ),
      validate: validateLookupField('id_mine_origin'),
    },
    {
      editable: 'always',
      title: translate('common:OperationType'),
      field: 'id_operation_type',
      required: true,
      customType: 'filterLookup',
      lookup: operationTypes.filter(e => e.id_equipment_type === filters.equipmentType),
      validate: validateLookupField('id_operation_type'),
    },
  ];

  const pastefillSpecificTitles = [
    {
      editable: 'always',
      title: translate('common:Destination'),
      field: 'id_destination',
      customType: 'filterLookup',
      lookup: elements,
      required: true,
      validate: validateLookupField('id_destination'),
    },
    {
      editable: 'always',
      title: translate('common:DestinationPoint'),
      field: 'id_destination_point',
      customType: 'filterLookup',
      lookup: subElements,
      lookupFilter: (option, row) => (
        option.id_element === row.id_destination || row.id_destination === 0
      ),
      required: true,
      validate: validateLookupField('id_destination_point'),
    },
  ];

  const projectRoboSpecificTitles = [
    {
      editable: 'always',
      title: translate('common:OperationType'),
      field: 'id_operation_type',
      required: true,
      customType: 'filterLookup',
      lookup: operationTypes.filter(e => e.id_equipment_type === filters.equipmentType),
      validate: validateLookupField('id_operation_type'),
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      field: 'id_mine',
      lookup: elements,
      required: true,
      customType: 'filterLookup',
      validate: validateLookupField('id_mine'),
    },
    {
      editable: 'always',
      title: translate('common:OriginPoint'),
      field: 'id_mine_origin',
      lookup: subElements,
      required: true,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        option.id_element === row.id_mine || row.id_mine === 0
      ),
      validate: validateLookupField('id_mine_origin'),
    },
  ];

  const setColumns = () => {
    const columnList = titles;
    if (MIXER === filters.equipmentType) columnList.push(...mixerSpecificTitles);
    if (PASTEFILL === filters.equipmentType) columnList.push(...pastefillSpecificTitles);
    if (PROJECTROBO === filters.equipmentType) columnList.push(...projectRoboSpecificTitles);

    return columnList;
  };

  const columns = setColumns();

  const customEditHistoryColumns = [
    {
      title: translate('common:Shift'),
      field: 'turn_id',
      lookup: turns,
    },
    {
      title: translate('common:Team'),
      field: 'team_id',
      lookup: teams,
    },
    {
      title: translate('common:OperatorGroup'),
      field: 'operator_group_id',
      lookup: operatorsGroups,
    },
    {
      title: translate('common:Operator'),
      field: 'operator_id',
      lookupKey: 'id_operator',
      lookup: operators,
    },
    {
      title: translate('common:EquipmentGroup'),
      field: 'equipament_groups_id',
      lookup: infrastructureGroups.filter(
        e => e.id_equipament === filters.equipmentType,
      ),
    },
    {
      title: translate('common:Equipment'),
      field: 'group_equipament_links_id',
      lookup: infrastructureEquips.filter(
        e => e.id_equip === filters.equipmentType,
      ),
    },
    {
      title: translate('common:Material'),
      field: 'material_id',
      lookup: materials,
    },
    {
      title: translate('common:EntryType'),
      field: 'exception_type',
      lookup: exceptionTypes,
    },
  ];

  const filterToFormFieldMap = {
    operatorGroup: 'operator_group_id',
    operator: 'operator_id',
    equipmentGroup: 'equipament_groups_id',
    equipment: 'group_equipament_links_id',
    startOrigin: 'id_mine',
    startSubOrigin: 'id_mine_origin',
    endOrigin: 'id_destination_point',
    endSubOrigin: 'id_destination',
    material: 'material_id',
    turn: 'turn_id',
  };

  const filterToFormMultiFieldMap = {
    equipmentGroup: 'equipament_groups_id',
    equipment: 'group_equipament_links_id',
  };

  const mappedFilterData = Object.entries(filterToFormFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      if (filters[filterKey] !== undefined && filters[filterKey] !== 0) {
        if (filterKey === 'codeType' || filterKey === 'codeGroup') {
          acc[formField] = Number(filters[filterKey]);
        } else {
          acc[formField] = filters[filterKey];
        }
      }
      return acc;
    },
    {},
  );

  const mappedFilterMultiData = Object.entries(filterToFormMultiFieldMap).reduce(
    (acc, [filterKey, formField]) => {
      const filterValues = filters[filterKey];

      // adiciona para initialFormData somente
      // se tiver apenas um item selecionado no multiselect
      if (filterValues && filterValues.length === 1) {
        const [firstValue] = filterValues;
        acc[formField] = firstValue;
      } else {
      // se tiver mais de um ou nenhum, seta como '0'
        acc[formField] = '0';
      }
      return acc;
    },
    {},
  );

  const initialFormData = {
    ...filters,
    ...mappedFilterData,
    ...mappedFilterMultiData,
    start_date: formatDate(newMomentDate(), true),
    end_date: formatDate(newMomentDate(), true),
    start_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    end_time: formatTime(newMomentDate(), 'HH:mm:ss'),
    duration: 0,
    idle_time: 0,
    code_time: 0,
    load_alt: 0,
    load_lat: 0,
    load_lon: 0,
    load_manager: 0,
    load_time: 0,
  };

  const removeUnnecessaryFields = (data) => {
    const result = data;
    const fieldsToRemove = [
      'startDate',
      'endDate',
      'turn',
      'code',
      'equipmentType',
      'operatorGroup',
      'operator',
      'material',
      'startOrigin',
      'endOrigin',
      'startSubOrigin',
      'endSubOrigin',
      'codeType',
      'codeGroup',
      'searchDailyParts',
      'hideInterruptedOrDeleted',
      'showOnlyReservedEquips',
      'equipment',
      'equipmentGroup',
      'touched',
      'handlingType',
    ];

    fieldsToRemove.forEach((field) => {
      delete result[field];
    });

    return result;
  };

  // só deve conseguir salvar todas as alterações caso todos os campos
  // obrigatórios das linhas alteradas sejam preenchidos.
  const disableSaveAllChangesButton = infrastructureList?.length ? infrastructureList
    .filter(row => row.touched)
    .flatMap(row => (
      Object.entries(row).map(([key, value]) => {
        if (key.startsWith('end_') || key.startsWith('start_')) return false;
        return columns.find(col => col.field === key)?.required && value === '';
      })
    ))
    .includes(true) : false;

  const { applyDefaultSort, ...paginationOptions } = usePagination({
    filters,
    dispatch,
    listType: 'infrastructure',
    pagination: infrastructurePagination,
    searchAction: searchInfrastructureList,
  });

  const { page = 0, total = 0, loadingMore = false } = infrastructurePagination || { };

  return (
    <>
      <PF2MDataTable
        title={translate('common:Cycles')}
        initialFormData={initialFormData}
        columns={applyDefaultSort(columns)}
        data={infrastructureList}
        page={page}
        totalCount={total}
        isLoading={loadingMore}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          showTitle: true,
          selection: true,
          search: true,
          cacheKey: 'daily_parts_infrastructure',
          maxBodyHeight: 'calc(100vh - 290px)',
          minBodyHeight: 'calc(100vh - 290px)',
          disableSaveButton: disableSaveAllChangesButton,
          showFirstLastPageButtons: false,
          ...paginationOptions,
        }}
        onAdd={newItem => dispatch(createInfrastructureItem(newItem))}
        onDiscard={() => dispatch(restoreOriginalInfrastructure())}
        onApply={() => {
          const parsedData = infrastructureList
            .filter(item => item.update_status)
            .map(removeUnnecessaryFields);

          const actionData = {
            data: parsedData,
            parameters: filters,
            saveLog: true,
            ogData: originalInfrastructureList,
          };
          dispatch(updateInfrastructureList(actionData));
        }}
        onChange={(newData) => {
          newData.forEach((item) => {
            dispatch(setInfrastructureValues({ ...item, touched: true }));
          });
        }}
        onDelete={(selectedRows) => {
          selectedRows.forEach((row) => {
            dispatch(deleteInfrastructureItem(row));
          });
        }}
      />
      <Modal
        open={openEditHistoryModal}
        onClose={() => setOpenEditHistoryModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditHistoryModal
          listName="infrastructure"
          referenceColumns={columns}
          customEditHistoryColumns={customEditHistoryColumns}
          selectedEditHistoryData={selectedEditHistoryData}
          handleClose={() => setOpenEditHistoryModal(false)}
        />
      </Modal>
    </>
  );
};


export default withStyles(styles)(InfrastructureList);
